import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./PrescriptionsReports.scss";
import { SkeletonBlock } from "../../Components/Skeleton/Skeleton";
import AvatarImage from "../../Components/AvatarImage/AvatarImage";
import AdminLayout from "../../Layout/AdminLayout";
import { clearPrescriptionError } from "../../Features/Auth/Slice/prescriptionSlice";
import {
  getAdminPrescriptions as getAdminPrescriptionsThunk,
  acceptAdminPrescription,
  rejectAdminPrescription,
} from "../../Features/Auth/Thunk/prescriptionThunk";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import { getAdminSocket } from "../../Api/socket";
import {
  getDealStageLabel,
  summarizePatientBids,
  groupBidRowsByProvider,
  combinedDealStageLabel,
} from "../../utils/dealStage";
import { showAlert } from "../../feedback/feedbackApi";

const TAB_IDS = ["prescription", "report", "insurance"];
const PAGE_LIMIT = 25;
const SEARCH_DEBOUNCE_MS = 400;

/** Matches backend `clinical-file-type.js`: insurance cards often uploaded on the reports step. */
const looksLikeInsuranceFileName = (item) => /insurance/i.test(String(item?.fileName || item?.raw?.originalName || ""));

/** Same bucketing as the former top-level tab filter (per-type list). */
function itemsOfClinicalTab(items, tabId) {
  let list = (Array.isArray(items) ? items : []).filter(
    (item) => String(item.type).toLowerCase() === tabId
  );
  if (tabId === "report") {
    list = list.filter((item) => !looksLikeInsuranceFileName(item));
  }
  return list;
}

function inferClinicalTabForItem(item) {
  const t = String(item.type || "").toLowerCase();
  if (t === "insurance") return "insurance";
  if (t === "report" && looksLikeInsuranceFileName(item)) return "insurance";
  if (t === "report") return "report";
  return "prescription";
}

/** Presigned or public URL for inline preview / modal (list API often omits previewImage). */
function clinicalFileViewUrl(item) {
  return String(
    item?.previewImage || item?.url || item?.raw?.fileUrl || item?.raw?.url || ""
  ).trim();
}

function inferClinicalFileKind(fileName) {
  const n = String(fileName || "").toLowerCase();
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(n)) return "image";
  if (/\.pdf$/i.test(n)) return "pdf";
  return "other";
}

export default function PrescriptionsReports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const highlightParam = searchParams.get("highlight") || "";
  const highlightMatchRequestId = highlightParam.startsWith("matchRequest_")
    ? highlightParam.slice("matchRequest_".length).trim() || null
    : null;
  const highlightPrescriptionId = highlightParam.startsWith("prescription_")
    ? highlightParam.replace("prescription_", "").trim() || null
    : highlightParam && !highlightMatchRequestId
      ? highlightParam.trim() || null
      : null;
  const highlightedRowRef = useRef(null);
  const lastAutoOpenedHighlightRef = useRef(null);

  const { documents, loading, error, pagination } = useSelector((state) => state.prescriptions);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [bidContextByPatient, setBidContextByPatient] = useState({});
  const [bidLoading, setBidLoading] = useState(false);
  const [openRequestKey, setOpenRequestKey] = useState(null);
  const [expandedGroupKey, setExpandedGroupKey] = useState(null);
  const [docAction, setDocAction] = useState(null);
  /** Tab inside an expanded care request (Prescriptions / Reports / Insurance). */
  const [requestDetailTab, setRequestDetailTab] = useState("prescription");
  /** Full-screen preview: image or PDF from presigned URL. */
  const [fileViewer, setFileViewer] = useState(null);
  /** Reject flow: collect reason in-app instead of `window.prompt`. */
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const rejectReasonInputRef = useRef(null);

  useEffect(() => {
    const locked = Boolean(fileViewer || rejectModal);
    if (!locked) return undefined;

    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (rejectModal) {
        setRejectModal(null);
        setRejectReason("");
        return;
      }
      setFileViewer(null);
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fileViewer, rejectModal]);

  useEffect(() => {
    if (!rejectModal) return;
    const id = window.requestAnimationFrame(() => {
      rejectReasonInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [rejectModal]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    dispatch(
      getAdminPrescriptionsThunk({
        status: "all",
        page: currentPage,
        limit: PAGE_LIMIT,
        search: searchQuery,
      })
    );
  }, [dispatch, currentPage, searchQuery]);

  useEffect(() => {
    return () => {
      dispatch(clearPrescriptionError());
    };
  }, [dispatch]);

  /** Refresh list when patient uploads or re-submits a file (admin Socket.IO). */
  useEffect(() => {
    const socket = getAdminSocket();
    if (!socket) return undefined;
    const onClinicalFileEvent = (payload) => {
      const t = String(payload?.type || "").toLowerCase();
      if (
        t === "prescription_resubmitted" ||
        t === "prescription_upload" ||
        t === "prescription_rejected" ||
        t === "prescription_accepted" ||
        t === "prescription_bulk_rejected" ||
        t === "prescription_bulk_accepted"
      ) {
        dispatch(
          getAdminPrescriptionsThunk({
            status: "all",
            page: currentPage,
            limit: PAGE_LIMIT,
            search: searchQuery,
          })
        );
      }
    };
    socket.on("notification:new", onClinicalFileEvent);
    return () => {
      try {
        socket.off("notification:new", onClinicalFileEvent);
      } catch (_) {}
    };
  }, [dispatch, currentPage, searchQuery]);

  const requestGroups = useMemo(() => {
    const byKey = new Map();
    for (const item of documents) {
      const uid = item.userId ? String(item.userId) : "";
      const mr = item.matchRequestId ? String(item.matchRequestId).trim() : "";
      const key = mr ? `mr:${mr}` : `pat:${uid || "unknown"}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          matchRequestId: mr || null,
          items: [],
        });
      }
      byKey.get(key).items.push(item);
    }
    const groups = [...byKey.values()].map((g) => {
      const sorted = [...g.items].sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      const cat = sorted.find((x) => x.requestCategorySubcategory)?.requestCategorySubcategory || "";
      return { ...g, items: sorted, requestCategorySubcategory: cat };
    });
    groups.sort((a, b) => {
      const ta = new Date(a.items[0]?.createdAt || 0).getTime();
      const tb = new Date(b.items[0]?.createdAt || 0).getTime();
      return tb - ta;
    });
    return groups;
  }, [documents]);

  const patientIds = useMemo(() => {
    const ids = new Set();
    documents.forEach((d) => {
      const uid = d.userId;
      if (uid) ids.add(String(uid));
    });
    return [...ids];
  }, [documents]);

  useEffect(() => {
    if (!patientIds.length) {
      setBidContextByPatient({});
      setBidLoading(false);
      return;
    }

    let cancelled = false;
    setBidLoading(true);

    (async () => {
      const next = {};
      try {
        await Promise.all(
          patientIds.map(async (pid) => {
            try {
              const res = await API.get(endpoints.ADMIN_BID_LOG, {
                params: { patientId: pid, page: 1, limit: 100 },
              });
              const rows = res?.data?.data ?? [];
              if (!cancelled) {
                next[pid] = summarizePatientBids(rows);
              }
            } catch {
              if (!cancelled) {
                next[pid] = {
                  totalBids: 0,
                  doctorCount: 0,
                  hospitalCount: 0,
                  rows: [],
                };
              }
            }
          })
        );
        if (!cancelled) setBidContextByPatient(next);
      } finally {
        if (!cancelled) setBidLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientIds]);

  useEffect(() => {
    if (!highlightMatchRequestId && !highlightPrescriptionId) {
      lastAutoOpenedHighlightRef.current = null;
      return;
    }
    if (!requestGroups.length) return;

    if (highlightMatchRequestId) {
      const found = requestGroups.find(
        (gr) => gr.matchRequestId && String(gr.matchRequestId) === String(highlightMatchRequestId)
      );
      if (!found) return;
      const token = `mr:${highlightMatchRequestId}:${found.key}`;
      if (lastAutoOpenedHighlightRef.current === token) return;
      lastAutoOpenedHighlightRef.current = token;
      setOpenRequestKey(found.key);
      const tabFromUrl = TAB_IDS.includes(tabParam) ? tabParam : null;
      const pickTab = () => {
        if (tabFromUrl && itemsOfClinicalTab(found.items, tabFromUrl).length) return tabFromUrl;
        for (const id of TAB_IDS) {
          if (itemsOfClinicalTab(found.items, id).length) return id;
        }
        return "prescription";
      };
      const nextTab = pickTab();
      setRequestDetailTab(nextTab);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.set("tab", nextTab);
          return n;
        },
        { replace: true }
      );
      return;
    }

    const found = requestGroups.find((gr) =>
      gr.items.some((item) => {
        const itemId = item.prescriptionId || item.id || item._id;
        return (
          String(itemId) === String(highlightPrescriptionId) ||
          String(item.id) === String(highlightPrescriptionId)
        );
      })
    );
    if (!found) return;
    const token = `rx:${highlightPrescriptionId}:${found.key}`;
    if (lastAutoOpenedHighlightRef.current === token) return;
    lastAutoOpenedHighlightRef.current = token;
    setOpenRequestKey(found.key);
    const highlightedItem = found.items.find((item) => {
      const itemId = item.prescriptionId || item.id || item._id;
      return (
        String(itemId) === String(highlightPrescriptionId) ||
        String(item.id) === String(highlightPrescriptionId)
      );
    });
    const nextTab = highlightedItem
      ? inferClinicalTabForItem(highlightedItem)
      : TAB_IDS.includes(tabParam)
        ? tabParam
        : "prescription";
    setRequestDetailTab(nextTab);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set("tab", nextTab);
        return n;
      },
      { replace: true }
    );
  }, [highlightMatchRequestId, highlightPrescriptionId, requestGroups, setSearchParams, tabParam]);

  useEffect(() => {
    if (expandedGroupKey && expandedGroupKey !== openRequestKey) setExpandedGroupKey(null);
  }, [openRequestKey, expandedGroupKey]);

  useEffect(() => {
    const hasHighlight = Boolean(highlightMatchRequestId || highlightPrescriptionId);
    if (!hasHighlight || !requestGroups.length || !highlightedRowRef.current) return;
    highlightedRowRef.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [highlightMatchRequestId, highlightPrescriptionId, requestGroups.length, loading, openRequestKey]);

  const goToSubmission = (item) => {
    const id = item.prescriptionId || item.id;
    if (!id) return;
    const submissionTab = inferClinicalTabForItem(item);
    navigate(
      `/patient-medical-submissions?prescriptionId=${encodeURIComponent(
        String(id)
      )}&tab=${encodeURIComponent(submissionTab)}&returnTo=clinical`
    );
  };

  const shortRequestId = (id) => {
    const s = String(id || "");
    if (!s) return "";
    if (s.length <= 10) return s;
    return `…${s.slice(-6)}`;
  };

  const docActionBusy = (prescriptionId) =>
    docAction &&
    String(docAction.id) === String(prescriptionId) &&
    (docAction.op === "accept" || docAction.op === "reject");

  const handleAcceptDoc = async (prescriptionId) => {
    if (!prescriptionId) return;
    setDocAction({ op: "accept", id: prescriptionId });
    try {
      await dispatch(acceptAdminPrescription(String(prescriptionId))).unwrap();
      await dispatch(
        getAdminPrescriptionsThunk({
          status: "all",
          page: currentPage,
          limit: PAGE_LIMIT,
          search: searchQuery,
        })
      ).unwrap();
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.payload || err?.message || "Failed to accept file";
      showAlert(msg, { variant: "error", title: "Accept failed" });
    } finally {
      setDocAction(null);
    }
  };

  const openRejectModal = (prescriptionId) => {
    if (!prescriptionId) return;
    setRejectReason("");
    setRejectModal({ prescriptionId: String(prescriptionId) });
  };

  const closeRejectModal = () => {
    setRejectModal(null);
    setRejectReason("");
  };

  const submitRejectFromModal = async () => {
    if (!rejectModal?.prescriptionId) return;
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      showAlert("Rejection reason is required.", { variant: "warning" });
      return;
    }
    const prescriptionId = rejectModal.prescriptionId;
    setDocAction({ op: "reject", id: prescriptionId });
    try {
      await dispatch(
        rejectAdminPrescription({
          prescriptionId: String(prescriptionId),
          rejectionReason: trimmed,
        })
      ).unwrap();
      await dispatch(
        getAdminPrescriptionsThunk({
          status: "all",
          page: currentPage,
          limit: PAGE_LIMIT,
          search: searchQuery,
        })
      ).unwrap();
      closeRejectModal();
    } catch (err) {
      const msg =
        typeof err === "string"
          ? err
          : err?.payload || err?.message || "Failed to reject file";
      showAlert(msg, { variant: "error", title: "Reject failed" });
    } finally {
      setDocAction(null);
    }
  };

  const tabLabel = (id) => {
    if (id === "prescription") return "Prescriptions";
    if (id === "report") return "Reports";
    return "Insurance";
  };

  const setRequestDetailTabInUrl = useCallback(
    (nextTab) => {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.set("tab", nextTab);
          return n;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const toggleRequestOpen = (key) => {
    setOpenRequestKey((prev) => {
      if (prev === key) {
        setExpandedGroupKey(null);
        setSearchParams(
          (p) => {
            const n = new URLSearchParams(p);
            n.delete("tab");
            return n;
          },
          { replace: true }
        );
        return null;
      }
      const nextTab = TAB_IDS.includes(tabParam) ? tabParam : "prescription";
      setRequestDetailTab(nextTab);
      setSearchParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.set("tab", nextTab);
          return n;
        },
        { replace: true }
      );
      return key;
    });
  };

  const openClinicalFilePreview = (item) => {
    const viewUrl = clinicalFileViewUrl(item);
    const kind = inferClinicalFileKind(item.fileName);
    if (viewUrl && (kind === "image" || kind === "pdf")) {
      setFileViewer({
        url: viewUrl,
        title: String(item.fileName || "Clinical file").trim() || "Clinical file",
        kind,
      });
      return;
    }
    if (viewUrl) {
      window.open(viewUrl, "_blank", "noopener,noreferrer");
      return;
    }
    goToSubmission(item);
  };

  const totalItems = Math.max(0, Number(pagination?.total) || 0);
  const totalPages = Math.max(1, Number(pagination?.totalPages) || 1);
  const hasPrevPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <AdminLayout>
      <>
      <div className="documentsPage">
        <div className="documentsToolbar">
          <label className="documentsSearchWrap" htmlFor="clinical-files-search">
            <span className="documentsSearchLabel">Search</span>
            <input
              id="clinical-files-search"
              type="search"
              className="documentsSearchInput"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Patient, mobile, file name, request id"
            />
          </label>
          <div className="documentsToolbarMeta">
            {!loading ? (
              <span>
                {totalItems} file{totalItems === 1 ? "" : "s"}
              </span>
            ) : null}
          </div>
        </div>

        <div className="documentsCard documentsCard--wide">
          <div className="documentsBody">
            {loading && (
              <div className="emptyDocuments">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} style={{ marginBottom: 10 }}>
                    <SkeletonBlock width="100%" height={58} />
                  </div>
                ))}
              </div>
            )}

            {!loading && error && <div className="emptyDocuments">{error}</div>}

            {!loading &&
              !error &&
              requestGroups.map((group) => {
                const first = group.items[0];
                const uid = first?.userId ? String(first.userId) : "";
                const ctx = uid ? bidContextByPatient[uid] : null;
                const allRows = ctx?.rows || [];
                const scopedRows = group.matchRequestId
                  ? allRows.filter(
                      (b) => String(b.matchRequestId || "") === String(group.matchRequestId)
                    )
                  : allRows;
                const bidCtx = summarizePatientBids(scopedRows);
                const rows = bidCtx.rows || [];
                const bidDetailsOpen = expandedGroupKey === group.key;
                const requestOpen = openRequestKey === group.key;
                const isRequestFromNotification =
                  Boolean(highlightMatchRequestId) &&
                  group.matchRequestId &&
                  String(group.matchRequestId) === String(highlightMatchRequestId);
                const openPatientReview = () => {
                  if (first) goToSubmission(first);
                };
                const pendingCount = group.items.filter((it) => {
                  const s = String(it.status || "pending").toLowerCase();
                  return s === "pending" || !s;
                }).length;
                const bidSummaryShort = !uid
                  ? "No patient link"
                  : rows.length === 0
                    ? group.matchRequestId
                      ? "No bids yet for this care request"
                      : "No bids yet for this patient"
                    : `${bidCtx.doctorCount} doctor${bidCtx.doctorCount === 1 ? "" : "s"} · ${bidCtx.totalBids} procedure match${bidCtx.totalBids === 1 ? "" : "es"}`;
                const tabItems =
                  requestOpen && openRequestKey === group.key
                    ? itemsOfClinicalTab(group.items, requestDetailTab)
                    : [];

                return (
                  <div
                    className={`documentRequestGroup${
                      isRequestFromNotification ? " documentRequestGroup--highlightRequest" : ""
                    }`}
                    key={group.key}
                  >
                    <div className="documentRequestMeta">
                      <span className="documentRequestMetaLabel">
                        {group.matchRequestId ? "Care request" : "Uploads not linked to a request"}
                      </span>
                      {group.matchRequestId ? (
                        <code className="documentRequestMetaId" title={group.matchRequestId}>
                          {shortRequestId(group.matchRequestId)}
                        </code>
                      ) : null}
                      {group.requestCategorySubcategory ? (
                        <span className="documentRequestMetaCat">{group.requestCategorySubcategory}</span>
                      ) : null}
                    </div>

                    <div
                      className={`documentRequestSummaryRow${
                        isRequestFromNotification ? " documentRequestSummaryRow--highlighted" : ""
                      }`}
                      ref={isRequestFromNotification ? highlightedRowRef : undefined}
                    >
                      <button
                        type="button"
                        className="documentRequestSummary"
                        onClick={() => toggleRequestOpen(group.key)}
                        aria-expanded={requestOpen}
                        aria-controls={`request-docs-${group.key}`}
                        id={`request-head-${group.key}`}
                      >
                        <div className="documentRequestSummaryMain">
                          <AvatarImage
                            src={first?.avatar}
                            alt={first?.patientName || "Patient"}
                            name={first?.patientName || "Patient"}
                            className="documentAvatar documentAvatar--summary"
                          />
                          <div className="documentUserInfo documentUserInfo--summary">
                            <h3>{first?.patientName || "Patient"}</h3>
                            {first?.location ? <p>{first.location}</p> : null}
                            {first?.patientMobile ? (
                              <p className="documentUserMeta">{first.patientMobile}</p>
                            ) : null}
                          </div>
                        </div>
                        <div className="documentRequestSummaryMid">
                          <span className="documentRequestFileCounts">
                            {group.items.length} file{group.items.length === 1 ? "" : "s"}
                            {pendingCount > 0 ? ` · ${pendingCount} pending review` : ""}
                          </span>
                          <span className="documentRequestBidTeaser">{bidSummaryShort}</span>
                        </div>
                        <span className={`documentRequestChevron${requestOpen ? " documentRequestChevron--open" : ""}`} aria-hidden>
                          ▼
                        </span>
                      </button>
                      <button type="button" className="documentRequestPatientLinkBtn" onClick={openPatientReview}>
                        Patient file
                      </button>
                    </div>

                    {requestOpen ? (
                      <div
                        className="documentRequestExpanded"
                        id={`request-docs-${group.key}`}
                        role="region"
                        aria-labelledby={`request-head-${group.key}`}
                      >
                        <div
                          className="documentsTabs documentsTabs--three documentsTabs--nested"
                          role="tablist"
                          aria-label="Clinical file types for this request"
                        >
                          {TAB_IDS.map((id) => (
                            <button
                              key={id}
                              type="button"
                              role="tab"
                              aria-selected={requestDetailTab === id}
                              className={`docTab docTab--nested ${requestDetailTab === id ? "active" : ""}`}
                              onClick={() => {
                                setRequestDetailTab(id);
                                setRequestDetailTabInUrl(id);
                              }}
                            >
                              {tabLabel(id)}
                            </button>
                          ))}
                        </div>
                        {!tabItems.length ? (
                          <div className="emptyDocuments emptyDocuments--nested">
                            No {tabLabel(requestDetailTab).toLowerCase()} for this request.
                          </div>
                        ) : (
                        <div className="documentRow documentRow--requestBundle documentRow--requestExpanded">
                          <div className="documentFileStack">
                            {tabItems.map((item) => {
                              const rxId = item.prescriptionId || item.id;
                              const itemId = item.prescriptionId || item.id || item._id;
                              const isHighlighted =
                                Boolean(highlightPrescriptionId) &&
                                (String(itemId) === String(highlightPrescriptionId) ||
                                  String(item.id) === String(highlightPrescriptionId));
                              const st = String(item.status || "pending").toLowerCase();
                              const statusLabel =
                                st === "accepted"
                                  ? "Accepted"
                                  : st === "rejected"
                                    ? "Rejected"
                                    : "Pending review";
                              const pending = st === "pending" || !st;
                              const busy = docActionBusy(rxId);
                              const viewUrl = clinicalFileViewUrl(item);
                              const fileKind = inferClinicalFileKind(item.fileName);
                              const showImageThumb = Boolean(viewUrl) && fileKind === "image";

                              return (
                                <div
                                  className={`documentDocRow${
                                    isHighlighted ? " documentDocRow--highlighted" : ""
                                  }`}
                                  key={String(item.prescriptionId || item.id || item._id)}
                                  ref={
                                    isHighlighted && !highlightMatchRequestId ? highlightedRowRef : null
                                  }
                                >
                                  <div className="documentFile">
                                    <div
                                      className={`fileMainRow${
                                        showImageThumb ? " fileMainRow--withThumb" : ""
                                      }`}
                                    >
                                      {showImageThumb ? (
                                        <button
                                          type="button"
                                          className="clinicalFileThumbBtn"
                                          onClick={() => openClinicalFilePreview(item)}
                                          aria-label={`Preview ${item.fileName || "file"}`}
                                        >
                                          <img
                                            className="clinicalFileThumb"
                                            src={viewUrl}
                                            alt=""
                                            loading="lazy"
                                          />
                                        </button>
                                      ) : (
                                        <div className="fileTypeIcon" aria-hidden>
                                          📄
                                        </div>
                                      )}
                                      <div className="fileInfo">
                                        <h4>{item.fileName}</h4>
                                        {item.fileSize ? <span>{item.fileSize}</span> : null}
                                      </div>
                                    </div>

                                    <div
                                      className={`fileMetaRow fileMetaRow--approval${
                                        showImageThumb ? " fileMetaRow--withThumb" : ""
                                      }`}
                                    >
                                      <p>{item.uploadedAt}</p>
                                      <span
                                        className={`docStatusPill docStatusPill--${pending ? "pending" : st}`}
                                      >
                                        {statusLabel}
                                      </span>
                                      <div className="fileMetaActions">
                                        <button
                                          type="button"
                                          className="downloadBtn"
                                          onClick={() => openClinicalFilePreview(item)}
                                        >
                                          View
                                        </button>
                                        {pending ? (
                                          <>
                                            <button
                                              type="button"
                                              className="docApproveBtn"
                                              disabled={busy}
                                              onClick={() => handleAcceptDoc(rxId)}
                                            >
                                              {busy && docAction?.op === "accept"
                                                ? "Approving…"
                                                : "Approve"}
                                            </button>
                                            <button
                                              type="button"
                                              className="docRejectBtn"
                                              disabled={busy}
                                              onClick={() => openRejectModal(rxId)}
                                            >
                                              {busy && docAction?.op === "reject"
                                                ? "Rejecting…"
                                                : "Reject"}
                                            </button>
                                          </>
                                        ) : null}
                                      </div>
                                    </div>
                                    {st === "rejected" && item.rejectionReason ? (
                                      <p className="documentRejectionNote">{item.rejectionReason}</p>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <div className="documentDeal">
                            {bidLoading && uid && !ctx ? (
                              <div className="documentDealLoading">Loading bids…</div>
                            ) : !uid ? (
                              <div className="documentDealEmpty">No patient link</div>
                            ) : rows.length === 0 ? (
                              <div className="documentDealEmpty">
                                {group.matchRequestId
                                  ? "No bids yet for this care request"
                                  : "No bids yet for this patient"}
                              </div>
                            ) : (
                              <>
                                {!group.matchRequestId ? (
                                  <p className="documentDealScopeNote">
                                    All bids for this patient (files are not tied to a single request).
                                  </p>
                                ) : null}
                                <div className="documentDealSummary">
                                  <span className="documentDealPill documentDealPill--doctors">
                                    {bidCtx.doctorCount} doctor{bidCtx.doctorCount === 1 ? "" : "s"}
                                  </span>
                                  {bidCtx.hospitalCount > 0 ? (
                                    <span className="documentDealPill documentDealPill--hospitals">
                                      {bidCtx.hospitalCount} hospital{bidCtx.hospitalCount === 1 ? "" : "s"}
                                    </span>
                                  ) : null}
                                  <span className="documentDealPill documentDealPill--muted">
                                    {bidCtx.totalBids} procedure match{bidCtx.totalBids === 1 ? "" : "es"}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="documentDealToggle"
                                  onClick={() => setExpandedGroupKey(bidDetailsOpen ? null : group.key)}
                                  aria-expanded={bidDetailsOpen}
                                >
                                  {bidDetailsOpen ? "Hide provider details" : "Show provider details"}
                                </button>
                                {bidDetailsOpen && (
                                  <ul className="documentDealList">
                                    {groupBidRowsByProvider(rows).map((g) => {
                                      const gKey = `${g.providerRole}-${String(g.providerId)}`;
                                      const single = g.lines.length === 1;
                                      const b0 = g.lines[0];
                                      return (
                                        <li key={gKey}>
                                          <div className="documentDealListHead">
                                            <span className="documentDealProvider">
                                              {g.providerRole === "doctor" ? "Dr." : "Hospital"}{" "}
                                              {g.providerName || "—"}
                                            </span>
                                            <span className="documentDealStage">
                                              {single ? getDealStageLabel(b0) : combinedDealStageLabel(g.lines)}
                                            </span>
                                          </div>
                                          {single ? (
                                            <div className="documentDealListMeta">
                                              {(b0.subCategoryName || b0.categoryName) && (
                                                <span>{b0.subCategoryName || b0.categoryName}</span>
                                              )}
                                              {b0.affordabilityPrice != null && (
                                                <span>Patient budget ₹{b0.affordabilityPrice}</span>
                                              )}
                                              {b0.reverseBidAmount != null && (
                                                <span>Counter ₹{b0.reverseBidAmount}</span>
                                              )}
                                            </div>
                                          ) : (
                                            <ul className="documentDealProcedureList">
                                              {g.lines.map((b) => (
                                                <li key={String(b.bidId)}>
                                                  <div className="documentDealListMeta documentDealListMeta--sub">
                                                    {(b.subCategoryName || b.categoryName) && (
                                                      <span>{b.subCategoryName || b.categoryName}</span>
                                                    )}
                                                    <span className="documentDealListMetaStage">
                                                      {getDealStageLabel(b)}
                                                    </span>
                                                    {b.affordabilityPrice != null && (
                                                      <span>Patient budget ₹{b.affordabilityPrice}</span>
                                                    )}
                                                    {b.reverseBidAmount != null && (
                                                      <span>Counter ₹{b.reverseBidAmount}</span>
                                                    )}
                                                  </div>
                                                </li>
                                              ))}
                                            </ul>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}

            {!loading && !error && requestGroups.length === 0 && (
              <div className="emptyDocuments">No clinical files found.</div>
            )}

            {!loading && !error && totalItems > 0 ? (
              <div className="documentsPagination">
                <button
                  type="button"
                  className="documentsPageBtn"
                  disabled={!hasPrevPage}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="documentsPageText">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="documentsPageBtn"
                  disabled={!hasNextPage}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {fileViewer ? (
        <div
          className="clinicalFileModalOverlay"
          onClick={() => setFileViewer(null)}
          role="presentation"
        >
          <div
            className="clinicalFileModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="clinical-file-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="clinicalFileModalHead">
              <h2 id="clinical-file-modal-title" className="clinicalFileModalTitle">
                {fileViewer.title}
              </h2>
              <button
                type="button"
                className="clinicalFileModalClose"
                onClick={() => setFileViewer(null)}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <div className="clinicalFileModalBody">
              {fileViewer.kind === "image" ? (
                <img
                  src={fileViewer.url}
                  alt={fileViewer.title}
                  className="clinicalFileModalImg"
                />
              ) : (
                <iframe
                  title={fileViewer.title}
                  src={fileViewer.url}
                  className="clinicalFileModalFrame"
                />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {rejectModal ? (
        <div
          className="rejectReasonModalOverlay"
          onClick={closeRejectModal}
          role="presentation"
        >
          <div
            className="rejectReasonModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-reason-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rejectReasonModalHead">
              <h2 id="reject-reason-modal-title" className="rejectReasonModalTitle">
                Reject clinical file
              </h2>
              <button
                type="button"
                className="rejectReasonModalClose"
                onClick={closeRejectModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="rejectReasonModalBody">
              <p className="rejectReasonModalHint">
                A short reason is required so the patient and care team understand why this file was
                not accepted.
              </p>
              <label className="rejectReasonModalLabel" htmlFor="reject-reason-textarea">
                Rejection reason
              </label>
              <textarea
                id="reject-reason-textarea"
                ref={rejectReasonInputRef}
                className="rejectReasonModalTextarea"
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Illegible handwriting, wrong document type, expired date…"
                disabled={docActionBusy(rejectModal.prescriptionId)}
              />
            </div>
            <div className="rejectReasonModalFooter">
              <button
                type="button"
                className="rejectReasonModalCancel"
                onClick={closeRejectModal}
                disabled={docActionBusy(rejectModal.prescriptionId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rejectReasonModalConfirm"
                onClick={() => void submitRejectFromModal()}
                disabled={docActionBusy(rejectModal.prescriptionId)}
              >
                {docActionBusy(rejectModal.prescriptionId) ? "Rejecting…" : "Reject file"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      </>
    </AdminLayout>
  );
}
